import { useState, FormEvent, ChangeEvent } from 'react';
import MicrosoftHttpService from '../../AppSettings/services/MicrosoftHttpService';
import { i18nService } from '../../AppSettings/services/i18nService';
import type { NewContact } from '../../types/team';

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactCreated: () => void;
}

const INITIAL_CONTACT: NewContact = {
  givenName: '',
  surname: '',
  emailAddresses: [{ address: '' }],
  mobilePhone: '',
  companyName: '',
  jobTitle: '',
};

const CreateContactModal = ({
  isOpen,
  onClose,
  onContactCreated,
}: CreateContactModalProps): JSX.Element | null => {
  const [newContact, setNewContact] = useState<NewContact>(INITIAL_CONTACT);

  const handleCreateContact = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const microsoftService = new MicrosoftHttpService([
        'https://graph.microsoft.com/Contacts.ReadWrite',
      ]);

      const contactData = {
        ...newContact,
        displayName: `${newContact.givenName} ${newContact.surname}`.trim(),
      };

      await microsoftService.createContact(contactData);
      await onContactCreated();

      setNewContact(INITIAL_CONTACT);
      onClose();
    } catch (error) {
      console.error(i18nService.t('errors.services.contacts.creation'), error);
      alert(i18nService.t('errors.services.contacts.creationTryAgain'));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="team-new-contact-form-overlay">
      <div className="team-new-contact-form">
        <h3>{i18nService.t('team.contacts.createNewContact')}</h3>
        <form onSubmit={handleCreateContact}>
          <div className="team-form-group">
            <input
              type="text"
              placeholder={i18nService.t('team.contacts.formCreate.firstName')}
              value={newContact.givenName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setNewContact({
                  ...newContact,
                  givenName: e.target.value,
                })
              }
              required
            />
          </div>
          <div className="team-form-group">
            <input
              type="text"
              placeholder={i18nService.t('team.contacts.formCreate.lastName')}
              value={newContact.surname}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setNewContact({
                  ...newContact,
                  surname: e.target.value,
                })
              }
              required
            />
          </div>
          <div className="team-form-group">
            <input
              type="email"
              placeholder={i18nService.t('team.contacts.formCreate.email')}
              value={newContact.emailAddresses[0].address}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setNewContact({
                  ...newContact,
                  emailAddresses: [{ address: e.target.value }],
                })
              }
              required
            />
          </div>
          <div className="team-form-group">
            <input
              type="tel"
              placeholder={i18nService.t('team.contacts.formCreate.mobilePhone')}
              value={newContact.mobilePhone}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setNewContact({
                  ...newContact,
                  mobilePhone: e.target.value,
                })
              }
            />
          </div>
          <div className="team-form-group">
            <input
              type="text"
              placeholder={i18nService.t('team.contacts.formCreate.company')}
              value={newContact.companyName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setNewContact({
                  ...newContact,
                  companyName: e.target.value,
                })
              }
            />
          </div>
          <div className="team-form-group">
            <input
              type="text"
              placeholder={i18nService.t('team.contacts.formCreate.jobTitle')}
              value={newContact.jobTitle}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setNewContact({
                  ...newContact,
                  jobTitle: e.target.value,
                })
              }
            />
          </div>
          <div className="team-form-actions">
            <button type="submit" className="team-submit-button">
              {i18nService.t('team.contacts.buttons.createContact')}
            </button>
            <button
              type="button"
              className="team-cancel-button"
              onClick={onClose}
            >
              {i18nService.t('generic.buttons.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateContactModal;
